import type { ISubject } from '@dakedake/types'
import type { Images, TvShowDetails } from '@/pages/Content/Subject/types'
import { modelName } from '@dakedake/types'
import { message } from 'antd'
import dayjs from 'dayjs'
import md5 from 'md5'
import { attachmentAdd, attachmentEdit, getTmdb, subjectAdd, subjectName } from '@/services'

const picObj = {
  backdrop: 'backdrops',
  posters: 'posters',
  logos: 'logos'
}

const DOT_OR_SLASH_REGEX = /\.|\//

interface ItmdbPic {
  url: string // 图片地址
  sid: number // 关联模型id
  type: string // 图片类型
  aid?: number // 关联数据id
  apiType?: string // api类型如：tv movie network company
  apiType2?: string // api类型如：tv movie network company
  apiType3?: string // api类型如：tv movie network company
  typeId?: number // api 类型id: 电影id 电视剧id 网络平台id 公司id
  typeId2?: number // api类型id: 电影id 电视剧id 网络平台id 公司id
  typeId3?: number // api类型id: 电影id 电视剧id 网络平台id 公司id
  noTmdb?: boolean // 是否不查询tmdb
}

const cacheData = {} as Record<string, Images>
/**
 * 上传图片
 * @param data ItmdbPic
 * @url 示例 https://image.tmdb.org/t/p/original/6S7G3FZnma2vo4B8ODqAdbeRKda.jpg https://image.tmdb.org/t/p/w600_and_h900_bestv2/6S7G3FZnma2vo4B8ODqAdbeRKda.jpg
 * @url 电影示例 https://gushi.zeabur.app/tmdb/movie/550/images?lang=111
 * @url 电视剧示例 https://gushi.zeabur.app/tmdb/tv/127532/images?lang=111
 * @url 网络平台示例 https://gushi.zeabur.app/tmdb/network/213/images?lang=111
 * @url 公司示例 https://gushi.zeabur.app/tmdb/company/1/images?lang=111
 * @url 剧集示例 https://gushi.zeabur.app/tmdb/tv/127532/season/1/episode/1/images?lang=111
 */
export async function tmdbPic(data: ItmdbPic): Promise<number> {
  const { url, sid, type, apiType, apiType2, apiType3, typeId, typeId2, typeId3, noTmdb = false } = data || {}
  if (!url)
    return 0
  const arrUrl = url.split('?')[0].split(DOT_OR_SLASH_REGEX)
  const file_name = arrUrl[arrUrl.length - 2] || ''
  const file_type = arrUrl.at(-1) || ''
  const params = { is_remote: true, file_path: url, md5: md5(url), sid, file_name, file_type, type, file_width: 0, file_height: 0 }
  if (!noTmdb) {
    if (!cacheData[url]) {
      const images = await getTmdb({ path: `${apiType}/${typeId}${apiType2 ? `/${apiType2}` : ''}${typeId2 !== undefined ? `/${typeId2}` : ''}${apiType3 ? `/${apiType3}` : ''}${typeId3 ? `/${typeId3}` : ''}/images?lang=1` })
      cacheData[url] = images || {}
    }
    const images = cacheData[url] || {}
    const { id, ...imagesData } = images
    console.log(id, 'tmdb图片数据')
    const types = picObj[type as keyof typeof picObj] as keyof typeof imagesData
    const imageData = imagesData[types]?.find(item => item.file_path === url)
    if (imageData) {
      const { height, width, file_type: f_type } = imageData || {}
      params.file_width = width || 0
      params.file_height = height || 0
      params.file_type = f_type || file_type
    }
  }

  const pic = await attachmentAdd(params)
  return pic?.data?.id || 0
}

export async function getTvDetail({ path, setLoading, data, type, createData, bangumi }: { path: string, setLoading?: any, data?: ISubject, type?: string, createData?: any, bangumi?: any }) {
  setLoading?.(true)
  /**
   * 详情 https://gushi.zeabur.app/tmdb/tv/85937
   */
  try {
    const res = await getTmdb({ path }) as TvShowDetails
    if (res) {
      if (type === 'score') {
        setLoading?.(false)
        return res
      }
      const {
        name, original_name, poster_path, backdrop_path, overview, episode_run_time, number_of_episodes, in_production, original_language, origin_country,
        number_of_seasons, first_air_date, homepage, last_air_date, vote_average, vote_count
      } = res

      if (name && !data) {
        const result = await subjectName({ name })
        if (result.data) {
          setLoading?.(false)
          return message.warning('名称已存在')
        }
      }

      const tmdbId = +path.split('/')[1]

      const detail = data
        ? {} as { data: ISubject }
        : await subjectAdd({
            name,
            number_of_seasons,
            title: original_name,
            cid: 2,
            type: 1,
            alias: createData?.alias || bangumi?.alias || '',
            website: homepage || createData?.website || bangumi?.website || '',
            cover: poster_path ? await tmdbPic({ url: poster_path, sid: modelName.SUBJECT, type: 'posters', apiType: 'tv', typeId: tmdbId }) : 0,
            backdrop: backdrop_path ? await tmdbPic({ url: backdrop_path, sid: modelName.SUBJECT, type: 'backdrop', apiType: 'tv', typeId: tmdbId }) : 0,
            content: overview,
            length: episode_run_time || [],
            total: number_of_episodes,
            isend: in_production ? 'completed' : 'unfinished',
            language: [original_language],
            area: origin_country,
            weekday: [dayjs(first_air_date).day() === 0 ? 7 : dayjs(first_air_date).day()],
            year: `${dayjs(first_air_date).year()}`,
            first_date: first_air_date,
            last_date: last_air_date,
            tag: bangumi?.bangumi?.meta_tags?.filter((item: string) => ['WEB', 'web', 'TV', 'tv', '日本', '中国'].includes(item) === false) || [],
            resources: [{
              site: 'tmdb',
              type: 'data',
              id: tmdbId,
              score: vote_average,
              count: vote_count
            }, ...([createData?.resources, bangumi?.resources].filter(Boolean) || [])]
          })

      const { id, cover, backdrop, sid } = data || detail.data || {}
      // https://api.themoviedb.org/3/tv/67063/season/1  /tv/{tv_id}/season/{season_number}
      if (id) {
        for await (const item of [{ type: 'posters', id: cover }, { type: 'backdrop', id: backdrop }])
          await attachmentEdit({ aid: id, id: item.id, type: item.type, sid })
      }

      setLoading?.(false)
      return detail
    }

    setLoading?.(false)
  }
  catch {
    setLoading?.(false)
  }
}
